import { classNames, cn } from "@/lib/utils";
import Tooltip from "@components/library/Tooltip";
import { InputName, OutputName, ToolName } from "@components/llama/VariableName";
import { Immutable } from "immer";
import { ToolResult } from "services/v4.service";
import { inputvalue, inputvariable, toolvalue, toolvariable } from "./PromptEditorDecorators";
import {
  extractInputIndicesFromPrompt,
  extractToolIndicesFromPrompt,
  extractRenderHighlights,
  Extraction,
} from "./utils";
import { SafeMarkdown } from "@components/llama/SafeMarkdown";
import { VariablesWithValues } from "@/services/logs.service";
import { PRIVACY_CLASSNAMES } from "@/lib/analytics/shared";
import { renderString } from "nunjucks";
import { TemplateLanguage } from "@/types/app/prompt";

// This is not a react component so it can be used react-truncate-markup which does not collapse react components
// See https://github.com/patrik-piskay/react-truncate-markup/issues/12#issuecomment-444761758
export const formatPromptTemplateHTML = (prompt: string): JSX.Element => {
  // Returns styled HTML to display the prompt
  if (!prompt) {
    return <span className="font-content"></span>;
  }
  const inputIndices = extractInputIndicesFromPrompt(prompt);
  const toolIndices = extractToolIndicesFromPrompt(prompt);

  // Join both and npsort by start index
  const allIndices = [...inputIndices, ...toolIndices].sort((a, b) => a.start - b.start);

  const outputBlocks = [];
  let lastEnd = 0;
  for (const { start, end, text, type } of allIndices) {
    if (start > lastEnd) {
      outputBlocks.push(
        <span className="whitespace-pre-wrap" key={lastEnd}>
          {prompt.slice(lastEnd, start)}
        </span>,
      );
    }
    if (type === "tool") {
      outputBlocks.push(toolvariable(prompt.slice(start, end), `${start}-${end}`));
    } else {
      outputBlocks.push(inputvariable(prompt.slice(start, end), `${start}-${end}`));
    }
    lastEnd = end;
  }

  if (lastEnd < prompt.length) {
    outputBlocks.push(
      <span className="whitespace-pre-wrap" key={lastEnd}>
        {prompt.slice(lastEnd)}
      </span>,
    );
  }

  return <span className="">{outputBlocks}</span>;
};

const fillPromptWithExtractions = (
  prompt: string,
  extractions: Extraction[],
  tool_results?: Immutable<ToolResult[]>,
): string => {
  let filledPrompt = "";
  let currentIndex = 0;
  let extractionsSorted = extractions.sort((a, b) => a.start - b.start);
  for (let { start, end, text, type, call } of extractionsSorted) {
    if (type === "tool") {
      const tool = tool_results?.find((t) => t.signature.split("(")[0] === call);
      if (tool && tool.result) {
        text = tool.result;
      }
    }
    filledPrompt += prompt.slice(currentIndex, start);
    filledPrompt += text;
    currentIndex = end;
  }
  filledPrompt += prompt.slice(currentIndex);

  return filledPrompt;
};

const getHighlights = (
  prompt: string,
  inputs?: VariablesWithValues,
  tool_results?: Immutable<ToolResult[]>,
  templateLanguage?: TemplateLanguage,
): { renderedPrompt: string; highlights: Extraction[] } => {
  let renderedPrompt = prompt;
  const toolIndices = extractToolIndicesFromPrompt(prompt);
  const inputIndices = extractInputIndicesFromPrompt(prompt);
  const inputIndicesFilled = inputIndices.map((e) => {
    const value = inputs?.[e.text] || "";
    return { ...e, text: value.toString() };
  });
  let allIndices: Extraction[] = [...inputIndicesFilled, ...toolIndices];

  switch (templateLanguage) {
    case "jinja": {
      // Fill our Prompt Tools upfront for jinja, otherwise it breaks the rendering - TODO we don't highlight tools here
      const filledPrompt = fillPromptWithExtractions(prompt, toolIndices, tool_results);
      try {
        renderedPrompt = renderString(filledPrompt, inputs || {});
        // For jinja we base highlighting on a diff, otherwise it's a nightmare
        allIndices = extractRenderHighlights(prompt, renderedPrompt);
      } catch (error) {
        // If there is an error in rendering, use the basic indices
      }
      break;
    }
    case "default":
      break;
    default:
      console.error("Unknown template language", templateLanguage);
      break;
  }

  return { renderedPrompt, highlights: allIndices.sort((a, b) => a.start - b.start) };
};

interface FormattedPromptProps {
  prompt: string;
  inputs?: VariablesWithValues;
  output?: string;
  tool_results?: Immutable<ToolResult[]>;
  templateLanguage?: TemplateLanguage;
  showAsMarkdown?: boolean;
  size?: "xs" | "sm";
}

export const FormattedPrompt = ({
  prompt,
  inputs,
  output,
  tool_results,
  templateLanguage,
  showAsMarkdown,
  size = "xs",
}: FormattedPromptProps) => {
  // Returns styled JSX to display the prompt outside of draftjs editor.
  // Replaces the {{key}} with the value from the data objects

  const { renderedPrompt, highlights } = getHighlights(prompt, inputs, tool_results, templateLanguage);

  const blocks = [];
  let string = "";
  let lastEnd = 0;

  for (const variable of highlights) {
    const { start, end, type } = variable;

    if (start > lastEnd) {
      blocks.push(
        <span className="" key={lastEnd}>
          {renderedPrompt.slice(lastEnd, start)}
        </span>,
      );
      string += renderedPrompt.slice(lastEnd, start) || "";
    }
    if (type === "tool") {
      // Look up the tool result.
      const tool = tool_results?.find((t) => t.signature.split("(")[0] === variable.call);
      const toolResult = tool?.result;
      if (toolResult) {
        blocks.push(
          <Tooltip side="top" content={<ToolName name={variable?.call || ""} />} key={start}>
            {toolvalue(toolResult, `${start}-${end}`, variable.call)}
          </Tooltip>,
        );
        string += toolResult;
      } else {
        const originalText = renderedPrompt.slice(start, end);
        blocks.push(
          <Tooltip side="top" content={<ToolName name={originalText} />} key={start}>
            {toolvalue(originalText, `${start}-${end}`, variable.call)}
          </Tooltip>,
        );
        string += originalText;
      }
    } else {
      const value = variable.text || "";
      blocks.push(
        <Tooltip side="top" content={<InputName name={variable.text} />} key={start}>
          {inputvalue(value.toString() || "", `${start}-${end}`, variable.text)}
        </Tooltip>,
      );
      string += value;
    }

    lastEnd = end;
  }

  if (lastEnd < prompt.length) {
    blocks.push(
      <span className="" key={lastEnd}>
        {renderedPrompt.slice(lastEnd)}
      </span>,
    );
    string += renderedPrompt.slice(lastEnd) || "";
  }
  if (output) {
    blocks.push(
      <Tooltip side="top" content={<OutputName />} key="output">
        <span key={"output"} className=" bg-oldgreen-200 px-1">
          {output}
        </span>
      </Tooltip>,
    );
  }
  string += output || "";

  return (
    <div className={classNames("font-content relative", PRIVACY_CLASSNAMES["high"])}>
      {showAsMarkdown ? (
        <div className={cn("prose -mt-16", size === "sm" ? "prose-sm" : "prose-xs")}>
          <SafeMarkdown>{string}</SafeMarkdown>
        </div>
      ) : (
        <div className="whitespace-pre-wrap break-words">{blocks}</div>
      )}
    </div>
  );
};

export const formattedPromptHtml = ({
  prompt,
  inputs,
  output,
  tool_results,
  templateLanguage,
}: FormattedPromptProps) => {
  const { renderedPrompt, highlights } = getHighlights(prompt, inputs, tool_results, templateLanguage);
  const blocks = [];
  let string = "";
  let lastEnd = 0;
  for (const variable of highlights) {
    const { start, end } = variable;
    if (start > lastEnd) {
      blocks.push(
        <span className="" key={lastEnd}>
          {renderedPrompt.slice(lastEnd, start)}
        </span>,
      );
    }
    if (variable.type === "tool") {
      // TODO: /WARNING: The ToolResult's signature has the predefined variable names
      // as given during setup. We're matching that against an arbitrary prompt where these
      // things can be renamed, so we only match on the first part of the signature.
      // TODO: extend ToolResult with the values that were used and whatever else is needed.
      const tool = tool_results?.find((t) => t.signature.split("(")[0] === variable.call);
      if (tool?.result) {
        blocks.push(toolvalue(tool.result, `${start}-${end}`, variable.call));
      } else {
        blocks.push(toolvariable(renderedPrompt.slice(start, end), `${start}-${end}`, variable.text));
      }
    } else {
      blocks.push(inputvalue(variable.text.toString(), `${start}-${end}`, variable.text));
    }

    lastEnd = end;
  }

  if (lastEnd < renderedPrompt.length) {
    blocks.push(
      <span className="" key={lastEnd}>
        {renderedPrompt.slice(lastEnd)}
      </span>,
    );
    string += renderedPrompt.slice(lastEnd) || "";
  }
  if (output) {
    blocks.push(
      <span title={"output"} key={"output"} className=" bg-oldgreen-200 px-1">
        {output}
      </span>,
    );
  }
  string += output || "";

  return (
    <div className="font-content relative">
      <div className={classNames("whitespace-pre-wrap break-words", PRIVACY_CLASSNAMES["high"])}>{blocks}</div>
    </div>
  );
};
